import React from 'react';
import { Select, Modal } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Area, AreaChart, Legend } from 'recharts';
import dayjs from 'dayjs';
import FlowIcon from '../assets/Congestion.svg';
import '../styles/Proprieta.css';

const Flow = ({ arco, selectedDate, setSelectedDate, selectedData, radarSelected }) => {
  const [selectedRoadIndex, setSelectedRoadIndex] = React.useState(0);
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Group by hash if it's an array
  const isMultipleRoads = arco && Array.isArray(arco) && arco.length > 0;
  let roads = [];

  if (isMultipleRoads) {
    const roadsByHash = {};
    arco.forEach(element => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    roads = Object.keys(roadsByHash).map(hash => ({
      hash,
      elements: roadsByHash[hash]
    }));
  } else if (arco && !Array.isArray(arco)) {
    roads = [{ hash: arco.hash, elements: [arco] }];
  }

  // Get available dates from roads data
  const getAvailableDates = () => {
    if (!roads || roads.length === 0) return [];

    const dates = new Set();
    roads.forEach(road => {
      road.elements?.forEach(el => {
        // Handle both date formats:
        // - New format: "2024-08-01" (YYYY-MM-DD)
        // - Old format: "12,13,14-03-2024_0" (DD,DD,DD-MM-YYYY_0)
        if (el.day_group_label) {
          const datePart = el.day_group_label.split('_')[0];

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            // New format: already in YYYY-MM-DD
            dates.add(datePart);
          } else {
            // Old format: extract dates from DD,DD,DD-MM-YYYY
            const dateMatch = datePart.match(/^([\d,]+)-(\d+)-(\d+)$/);
            if (dateMatch) {
              const days = dateMatch[1].split(',');
              const month = dateMatch[2].padStart(2, '0');
              const year = dateMatch[3];

              // Add each day as a separate date
              days.forEach(day => {
                const formattedDate = `${year}-${month}-${day.padStart(2, '0')}`;
                dates.add(formattedDate);
              });
            }
          }
        } else if (el.date) {
          dates.add(dayjs(el.date).format('YYYY-MM-DD'));
        }
      });
    });

    return Array.from(dates).sort();
  };

  const availableDates = getAvailableDates();

  // Set LAST date as default if no date is selected
  React.useEffect(() => {
    if (!selectedDate && availableDates.length > 0) {
      setSelectedDate(availableDates[availableDates.length - 1]);
    }
  }, [availableDates, selectedDate, setSelectedDate]);

  // Only show for roads, not radar
  if (radarSelected && (!arco || arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona una strada sulla mappa per visualizzare i dati di flusso.
          </p>
        </div>
      </div>
    );
  }

  if (!arco || (Array.isArray(arco) && arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona una strada sulla mappa per visualizzare i dati di flusso.
          </p>
        </div>
      </div>
    );
  }

  // Generate chart data for traffic flow
  const generateFlowChartData = () => {
    if (!selectedDate) {
      return selectedData || [];
    }

    // Filter to show only the selected road when multiple roads are selected
    const roadsToShow = roads.length > 1 ? [roads[selectedRoadIndex]] : roads;

    const dataPoints = [];

    roadsToShow.forEach((road, roadIndex) => {
      const roadElements = road.elements || [];

      const filteredElements = selectedDate
        ? roadElements.filter(el => {
          // Handle both date formats:
          // - New format: "2024-08-01" (YYYY-MM-DD)
          // - Old format: "12,13,14-03-2024_0" (DD,DD,DD-MM-YYYY_0)
          if (el.day_group_label) {
            const datePart = el.day_group_label.split('_')[0];

            // Check if it's new format (YYYY-MM-DD)
            const newFormatMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (newFormatMatch) {
              return datePart === selectedDate;
            } else {
              // Old format: extract dates from DD,DD,DD-MM-YYYY
              const dateMatch = datePart.match(/^([\d,]+)-(\d+)-(\d+)$/);
              if (dateMatch) {
                const days = dateMatch[1].split(',');
                const month = dateMatch[2].padStart(2, '0');
                const year = dateMatch[3];

                // Check if selectedDate matches any of the days
                return days.some(day => {
                  const formattedDate = `${year}-${month}-${day.padStart(2, '0')}`;
                  return formattedDate === selectedDate;
                });
              }
            }
          } else if (el.date) {
            const elDate = dayjs(el.date).format('YYYY-MM-DD');
            return elDate === selectedDate;
          }
          return false;
        })
        : roadElements;

      filteredElements.forEach(el => {
        // Extract time from metrics.json format
        const time = el.t_start || '00:00';

        // Use Flusso_Espanso (traffic flow) from metrics.json in VEH/15min
        // Divide by 3 as requested
        const flowValue = (parseFloat(el.Flusso_Espanso) || 0) / 3;

        dataPoints.push({
          time,
          Flow: Math.round(flowValue),
          roadIndex
        });
      });
    });

    // Sort by time
    dataPoints.sort((a, b) => {
      const [aHour, aMin] = a.time.split(':').map(Number);
      const [bHour, bMin] = b.time.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });

    return dataPoints;
  };

  const chartDataToDisplay = generateFlowChartData();
  const colors = ['#FF4D4D', '#FF852B', '#FFE100', '#8B7FD9'];

  // Render chart component
  const renderChart = (height = 300) => (
    <>
      {chartDataToDisplay?.length > 0 ? (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={chartDataToDisplay}>
            <defs>
              <linearGradient id="delayGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[selectedRoadIndex % colors.length]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={colors[selectedRoadIndex % colors.length]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              interval="preserveStartEnd"
              ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59']}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              label={{ value: 'VEH/15min', position: 'insideLeft', style: { fontSize: 12, fill: '#666' } }}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === 'Flow' || name.includes('Flow')) {
                  return [`${value} VEH/15min`, name];
                }
                return [value, name];
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="Flow"
              stroke={colors[selectedRoadIndex % colors.length]}
              strokeWidth={2}
              fill="url(#delayGradient)"
              name={roads.length > 1 ? `Strada ${selectedRoadIndex + 1}` : 'Flow (VEH/15min)'}
              connectNulls={true}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="chart-no-data">
          <p>⚠️ Nessun dato disponibile per questa strada nella data selezionata.</p>
          <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
            Prova a selezionare un'altra data.
          </p>
        </div>
      )}
    </>
  );

  // Check if road has any data at all
  const hasAnyData = availableDates.length > 0;

  return (
    <>
    <div className="proprieta-wrapper">
      <div className="proprieta-title">
        <img src={FlowIcon} alt="Flow" className="proprieta-icon-img" />
        <h3>Flow</h3>
      </div>

      {/* Road selector buttons - always visible when 2 roads are selected */}
      {roads.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 0',
          marginBottom: '8px'
        }}>
          <button
            onClick={() => setSelectedRoadIndex(0)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: selectedRoadIndex === 0 ? '2px solid #FF4D4D' : '1px solid #ddd',
              background: selectedRoadIndex === 0 ? '#FF4D4D20' : 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: selectedRoadIndex === 0 ? 'bold' : 'normal'
            }}
          >
            Strada 1
          </button>
          <button
            onClick={() => setSelectedRoadIndex(1)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: selectedRoadIndex === 1 ? '2px solid #FF852B' : '1px solid #ddd',
              background: selectedRoadIndex === 1 ? '#FF852B20' : 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: selectedRoadIndex === 1 ? 'bold' : 'normal'
            }}
          >
            Strada 2
          </button>
        </div>
      )}

      {/* Always show date selector and chart area */}
      <div className="proprieta-content anomaly-content">
        <p className="proprieta-description anomaly-description">
          Seleziona la data per la quale vuoi visualizzare i valori.
        </p>
        {hasAnyData ? (
          <Select
            placeholder="Seleziona data"
            className="anomaly-date-picker"
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            getPopupContainer={(trigger) => trigger.parentElement}
            popupStyle={{ zIndex: 9999 }}
            style={{  borderRadius: "20px" }}
          >
            {availableDates.map(date => (
              <Select.Option key={date} value={date}>
                {dayjs(date).format('DD/MM/YYYY')}
              </Select.Option>
            ))}
          </Select>
        ) : (
          <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
            Nessuna data disponibile per questa strada.
          </div>
        )}
      </div>

      {/* Flow Chart - Always show, with no-data message if needed */}
      <div style={{ position: 'relative' }}>
        {chartDataToDisplay?.length > 0 && (
          <button
            onClick={() => setIsExpanded(true)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '36px',
              height: '36px',
              backgroundColor: 'rgba(24, 144, 255, 0.9)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              zIndex: 100,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Espandi grafico"
          >
            <ExpandOutlined />
          </button>
        )}
        {renderChart()}
      </div>
    </div>

    {/* Expanded Chart Modal */}
    <Modal
      open={isExpanded}
      onCancel={() => setIsExpanded(false)}
      footer={null}
      width="95%"
      centered
      title="Flow - Vista Espansa"
      destroyOnClose
      zIndex={9999}
      style={{ top: 20 }}
    >
      <div style={{ padding: '20px 0' }}>
        {renderChart(500)}
      </div>
    </Modal>
    </>
  );
};

export default Flow;
